import React, { useState } from 'react';
import cx from 'classnames';

import Button from '~/components/button';
import LoadingIndicator from '~/components/loading-indicator';
import IncrementorField from '~/components/inputs/incrementor-field';
import { toLongMonthAndDate } from '~/utils/dates';
import { centsToCurrency } from '~/utils/numbers';
import { useCartMutations } from '~/features/cart/cart-actions';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { useShippingMessages } from '~/hooks/use-shipping-messages';
import { useProductBySku } from '~/features/products/products-actions';
import { reportMissingSanityImage, urlFor } from '~/sanity/images';
import { canSeeQtyField } from '~/features/products/products';
import { subscriptionType } from '~/features/subscriptions/subscription';

import styles from '~/components/cart/cart.module.scss';

const DEFAULT_PLAN_IMAGE = '/images/unboxing-bottom-400w.png?v1';
const DEFAULT_ITEM_IMAGE = '/images/logo-black.svg?v1';

const CartItem = ({ cartItem, interactive = true }) => {
  const { showFlashMessage } = useFlashMessage();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { updateCartQuantity, removeFromCart } = useCartMutations();

  const { product } = useProductBySku(cartItem?.sku?.skuId);

  const isPlan = cartItem.sku.isPlan;
  const isDiscounted = cartItem.sku.fullPrice !== cartItem.sku.unitPrice;

  const hasValidImage = Boolean(isPlan || product?.primaryImage);

  const canEditQuantity = canSeeQtyField(product, isPlan);

  const getItemImageUrl = () => {
    if (!hasValidImage) {
      return DEFAULT_ITEM_IMAGE;
    }

    if (
      isPlan &&
      cartItem.sku.subscriptionType === subscriptionType.LAWN_PLAN
    ) {
      return DEFAULT_PLAN_IMAGE;
    }

    const sanityImageUrl = urlFor(product?.primaryImage)
      .width(240)
      .height(240)
      .fit('max')
      .url();

    if (!sanityImageUrl) {
      reportMissingSanityImage(cartItem?.sku?.skuId);
    }

    // If the item is not a plan, use the image from Sanity or fallback to the subsoil image
    return sanityImageUrl || cartItem.sku.imageUrl || DEFAULT_ITEM_IMAGE;
  };

  const handleUpdateQuantity = async (qty) => {
    try {
      setIsUpdating(true);
      await updateCartQuantity(cartItem, qty);
    } catch (_) {
      showFlashMessage(
        `Oops, there was a problem updating the quantity for ${cartItem.sku.title}.`,
        'error',
        5000
      );
    }

    setIsUpdating(false);
  };

  const handleRemoveItem = async () => {
    try {
      setIsDeleting(true);
      await removeFromCart(cartItem);
      showFlashMessage(
        `${cartItem.sku.title} removed from your cart.`,
        'info',
        2500
      );
    } catch (_) {
      setIsDeleting(false);
      showFlashMessage(
        `Oops, there was a problem removing ${cartItem.sku.title} from your cart.`,
        'error',
        5000
      );
    }
  };

  const imageUrl = getItemImageUrl();

  return (
    <div className={styles.cartItem} data-testid="cart-item">
      <img
        className={styles.cartItemImg}
        style={{
          objectFit:
            hasValidImage && imageUrl !== DEFAULT_ITEM_IMAGE
              ? 'cover'
              : 'contain',
        }}
        src={imageUrl}
        alt={cartItem.sku.title}
        width="120"
        height="120"
      />

      <div className={styles.cartItemNameContainer}>
        <p className={styles.cartItemName}>{cartItem.sku.title}</p>
        <p className={styles.cartItemDescription}>
          {cartItem.sku.shortDescription}
        </p>
        <SeasonalShipping cartItemSku={cartItem?.sku} />
      </div>

      <span className={styles.quantity}>
        {canEditQuantity && interactive && (
          <IncrementorField
            input={{
              onChange: (e) => handleUpdateQuantity(e.target.value),
              value: cartItem.quantity,
              disabled: isUpdating,
            }}
          />
        )}
      </span>

      <span className={styles.totalPrice}>
        {isUpdating ? (
          <div className={styles.cartItemUpdatingLoader}>
            <LoadingIndicator />
          </div>
        ) : (
          <>
            {isDiscounted && (
              <span
                className={cx('strikeThrough', styles.strikethroughPrice)}
                data-testid="retail-price"
              >
                {centsToCurrency(cartItem.sku.fullPrice * cartItem.quantity, {
                  round: false,
                })}
              </span>
            )}
            <div className={styles.actualPrice} data-testid="actual-price">
              {centsToCurrency(cartItem.totalPrice, { round: false })}
            </div>
          </>
        )}
      </span>

      {interactive && (
        <Button
          className={styles.removeBtn}
          variant="link"
          onClick={handleRemoveItem}
          isLoading={isDeleting}
          aria-label={`Remove ${cartItem.sku.title} from cart`}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="var(--gray-4)"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M9 .667A8.326 8.326 0 00.667 9 8.326 8.326 0 009 17.333 8.326 8.326 0 0017.333 9 8.326 8.326 0 009 .667zm4.167 11.325l-1.175 1.175L9 10.175l-2.992 2.992-1.175-1.175L7.825 9 4.833 6.008l1.175-1.175L9 7.825l2.992-2.992 1.175 1.175L10.175 9l2.992 2.992z" />
          </svg>
        </Button>
      )}
    </div>
  );
};

export default CartItem;

export const SeasonalShipping = ({ className, cartItemSku }) => {
  const { skuId, earliestShipDate, title } = cartItemSku;

  // Smart Lawn Plan items don't return a provider, handled in the hook
  const { shippingMessage } = useShippingMessages(
    title.includes('Smart Lawn Plan') ? '' : skuId
  );

  if (!earliestShipDate) return null;

  const shipNow = Date.now() >= new Date(earliestShipDate).setHours(0, 0, 0, 0);

  const shippingText = shipNow
    ? `Ships in ${shippingMessage?.message}`
    : `Ships beginning ${toLongMonthAndDate(earliestShipDate)}`;

  return (
    <div className={cx(styles.shippingMessage, className)}>
      <img
        alt="Shipping box icon"
        className={styles.shippingIcon}
        src="/icons/sunday-store/shipping-box-icon.svg"
        width="20"
        height="20"
      />
      <p className={styles.shippingText}>{shippingText}</p>
    </div>
  );
};
